import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { ProfileAdd } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import BDrawer from '@ui/components/common/BDrawer';
import { PrimaryGrid } from '@ui/components/common/Grid';
// One line per person, each formatted as "Person N: Label: value, Label: value, ...". Splitting on
// ", " between pairs is a best-effort parse of a display string (not a real API contract) - fine for
// showing a readable table here, but this cell should never be used to feed data back into a form.
const parsePeople = (value) => {
    const lines = (value || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    return lines.map((line) => {
        const [personLabel, rest = ''] = line.split(/:(.*)/s).map((part) => part.trim());
        const valuesByColumn = {};
        rest
            .split(', ')
            .map((pair) => pair.trim())
            .filter(Boolean)
            .forEach((pair) => {
            const [label, fieldValue = ''] = pair.split(/:(.*)/s).map((part) => part.trim());
            if (label)
                valuesByColumn[label] = fieldValue;
        });
        return { personLabel, valuesByColumn };
    });
};
const AdditionalAttendeesCell = ({ value }) => {
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const people = useMemo(() => parsePeople(value), [value]);
    const fieldLabels = useMemo(() => Array.from(new Set(people.flatMap((person) => Object.keys(person.valuesByColumn)))), [people]);
    const columns = useMemo(() => {
        const personColumn = {
            field: 'person',
            headerName: t('familyForm.person', 'Person') || 'Person',
            minWidth: 120,
            flex: 0.8
        };
        const valueColumns = fieldLabels.map((label) => ({
            field: label,
            headerName: label,
            minWidth: 140,
            flex: 1
        }));
        return [personColumn, ...valueColumns];
    }, [fieldLabels, t]);
    const rows = useMemo(() => people.map((person, index) => ({
        id: index,
        person: person.personLabel,
        ...fieldLabels.reduce((acc, label) => {
            acc[label] = person.valuesByColumn[label] || '-';
            return acc;
        }, {})
    })), [people, fieldLabels]);
    if (people.length === 0) {
        return _jsx("span", { children: "-" });
    }
    const handleOpen = (event) => {
        event.stopPropagation();
        setDrawerOpen(true);
    };
    const handleClose = () => {
        setDrawerOpen(false);
    };
    return (_jsxs(_Fragment, { children: [_jsx(Chip, { size: 'small', variant: 'outlined', color: 'primary', icon: _jsx(ProfileAdd, { size: 16 }), onClick: handleOpen, label: t('events.eventDetails.responsesTab.additionalAttendeesCount', {
                    count: people.length,
                    defaultValue: '{{count}} additional attendee(s)'
                }), sx: { cursor: 'pointer' } }), _jsx(BDrawer, { open: drawerOpen, onClose: handleClose, anchor: 'bottom', title: t('events.additionalAttendeesPreset.title'), showBottomBar: false, fullHeight: true, children: _jsx(Box, { sx: { flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }, children: _jsx(PrimaryGrid, { columns: columns, rows: rows, showHeaderControls: false, showNewHeaderControls: false, showFilters: false, maxHeight: '100%', disableRowSelectionOnClick: true, noRowsText: t('events.eventDetails.responsesTab.noResponses', 'No responses found'), boxProps: {
                            sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                        } }) }) })] }));
};
export default AdditionalAttendeesCell;
